import { getLandingSegments, landingSegmentAnchor } from "../landingEditor/landingLayout";
import {
  editableLayoutSegment,
  customSectionDefinition,
  getAtPath,
  imageFieldsFromRegistry,
} from "./inspector/inspectorModel";
import {
  getSegmentDefinition,
  SEGMENT_FIELD_TYPES,
  SEGMENT_REGISTRY,
} from "./segmentRegistry";
import { clampHeroTextFontScale } from "./heroTextStyle";

const BUTTON_LABEL_PATTERN = /(CtaLabel|ctaLabel)$/;

export const SYNTHETIC_ELEMENT_DESCRIPTORS = Object.freeze({
  "navbar.mobile-menu": Object.freeze({ kind: "complex", label: "Menú móvil" }),
  "featured.cards": Object.freeze({ kind: "complex", label: "Tarjetas del catálogo" }),
});

function escapeRegExp(value) {
  return String(value || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

const SNAPSHOT_INSTANCE_TYPES = new Set(["imageCarousel", "testimonials"]);
const SNAPSHOT_INSTANCE_VISIBILITY_PATHS = Object.freeze({
  imageCarousel: "sections.showImageCarousel",
  testimonials: "sections.showTestimonials",
});

function sameSegmentIdentity(segmentId, sectionType) {
  return String(segmentId || "").trim().toLowerCase() === String(sectionType || "").trim().toLowerCase();
}
const SNAPSHOT_INSTANCE_PATH_KEYS = Object.freeze([
  "path",
  "fieldPath",
  "colorPath",
  "stylePrefix",
  "collectionPath",
  "altPath",
  "positionPath",
  "inheritedValuePath",
  "showPath",
  "mobileVariantPath",
  "labelPath",
  "urlPath",
]);

function rebaseSnapshotInstancePath(path, layoutIndex, sectionType) {
  const value = String(path || "");
  if (!value || layoutIndex < 0 || !SNAPSHOT_INSTANCE_TYPES.has(sectionType)) return value;
  const root = `layout.segments.${layoutIndex}`;
  if (value === SNAPSHOT_INSTANCE_VISIBILITY_PATHS[sectionType]) return `${root}.visible`;
  if (value === "theme.template") return `${root}.visualStyle`;
  if (value.startsWith(`${sectionType}.`)) return `${root}.data.${value}`;
  return value;
}

function rebaseSnapshotPathObject(source, layoutIndex, sectionType) {
  if (!source || typeof source !== "object") return source;
  const next = { ...source };
  SNAPSHOT_INSTANCE_PATH_KEYS.forEach((key) => {
    if (typeof next[key] === "string") {
      next[key] = rebaseSnapshotInstancePath(next[key], layoutIndex, sectionType);
    }
  });
  ["colorPathByDevice", "stylePrefixByDevice"].forEach((key) => {
    if (!next[key] || typeof next[key] !== "object" || Array.isArray(next[key])) return;
    next[key] = Object.fromEntries(
      Object.entries(next[key]).map(([device, value]) => [
        device,
        typeof value === "string" ? rebaseSnapshotInstancePath(value, layoutIndex, sectionType) : value,
      ])
    );
  });
  ["extraPaths", "fieldPaths", "visibilityCompanionPaths"].forEach((key) => {
    if (!Array.isArray(next[key])) return;
    next[key] = next[key].map((value) =>
      typeof value === "string" ? rebaseSnapshotInstancePath(value, layoutIndex, sectionType) : value
    );
  });
  if (next.item && typeof next.item === "object") {
    next.item = {
      ...next.item,
      ...(Array.isArray(next.item.fields)
        ? { fields: next.item.fields.map((field) => rebaseSnapshotPathObject(field, layoutIndex, sectionType)) }
        : {}),
    };
  }
  return next;
}

function snapshotInstanceDefinition(definition, layout, sectionType) {
  if (!definition || layout?.index < 0 || layout?.segment?.type !== sectionType) return definition;
  if (!SNAPSHOT_INSTANCE_TYPES.has(sectionType) || sameSegmentIdentity(layout.segment.id, sectionType)) return definition;
  const root = `layout.segments.${layout.index}`;
  return {
    ...definition,
    id: layout.segment.id,
    type: sectionType,
    visibilityPath: `${root}.visible`,
    defaultVisible: layout.segment.visible !== false,
    shellPropertyPaths: (definition.shellPropertyPaths || []).map((path) =>
      rebaseSnapshotInstancePath(path, layout.index, sectionType)
    ),
    shellAdvancedGroups: (definition.shellAdvancedGroups || []).map((group) => ({
      ...group,
      paths: (group?.paths || []).map((path) => rebaseSnapshotInstancePath(path, layout.index, sectionType)),
    })),
    visibilityCompanionPaths: (definition.visibilityCompanionPaths || []).map((path) =>
      rebaseSnapshotInstancePath(path, layout.index, sectionType)
    ),
    elementTargets: Object.fromEntries(
      Object.entries(definition.elementTargets || {}).map(([key, target]) => [
        key,
        rebaseSnapshotPathObject(target, layout.index, sectionType),
      ])
    ),
    properties: (definition.properties || []).map((property) =>
      rebaseSnapshotPathObject(property, layout.index, sectionType)
    ),
  };
}

export function getElementSectionContext(form, sectionId) {
  const layout = editableLayoutSegment(form, sectionId);
  if (layout.segment?.type === "custom") {
    return {
      sectionId,
      sectionType: "custom",
      layout,
      definition: customSectionDefinition(layout.segment, layout.index),
    };
  }
  const sectionType = layout.segment?.type || sectionId;
  const definition = getSegmentDefinition(sectionType);
  return {
    sectionId,
    sectionType,
    layout,
    definition: SNAPSHOT_INSTANCE_TYPES.has(sectionType)
      ? snapshotInstanceDefinition(definition, layout, sectionType)
      : definition,
  };
}

function resolveCanvasPath(context, canvasPath) {
  if (context.layout.index < 0) return canvasPath;
  if (context.sectionType === "custom") {
    const prefix = `layout.segments.${context.layout.index}.data.custom`;
    return String(canvasPath || "").startsWith("custom.")
      ? `${prefix}.${String(canvasPath).slice("custom.".length)}`
      : canvasPath;
  }
  if (SNAPSHOT_INSTANCE_TYPES.has(context.sectionType) && !sameSegmentIdentity(context.layout.segment?.id, context.sectionType)) {
    return rebaseSnapshotInstancePath(canvasPath, context.layout.index, context.sectionType);
  }
  return canvasPath;
}

function collectionMatch(properties, fieldPath) {
  for (const property of properties) {
    if (property?.type !== SEGMENT_FIELD_TYPES.COLLECTION || !property.path) continue;
    const match = String(fieldPath || "").match(
      new RegExp(`^${escapeRegExp(property.path)}\\.(\\d+)(?:\\.([^.]+))?$`)
    );
    if (!match) continue;
    const index = Number(match[1]);
    const itemKey = match[2] || "";
    const itemFields = Array.isArray(property.item?.fields) ? property.item.fields : [];
    const itemField = itemFields.find((candidate) => candidate.key === itemKey) || null;
    return { property, index, itemKey, itemField };
  }
  return null;
}

function buttonDescriptor(properties, field, fieldPath) {
  if (!field || !BUTTON_LABEL_PATTERN.test(fieldPath)) return null;
  const companionPath = (suffix) => fieldPath.replace(/Label$/, suffix);
  const companion = (suffix) => properties.find((candidate) => candidate.path === companionPath(suffix)) || null;
  const stylePrefix = companionPath("Style.");
  const dedicatedStyleFields = properties.filter((candidate) =>
    String(candidate.path || "").startsWith(stylePrefix)
  );
  return {
    kind: "button",
    label: field.label || "Botón",
    labelField: field,
    labelPath: fieldPath,
    urlField: companion("Url"),
    requiresProductsField: companion("RequiresProducts"),
    showIconField: companion("ShowIcon"),
    iconField: companion("Icon"),
    styleFields: dedicatedStyleFields,
    hasDedicatedStyle: dedicatedStyleFields.length > 0,
  };
}

function logicalKind(field) {
  if (!field) return "field";
  if (field.type === SEGMENT_FIELD_TYPES.IMAGE) return "image";
  if ([SEGMENT_FIELD_TYPES.TEXT, SEGMENT_FIELD_TYPES.TEXTAREA, SEGMENT_FIELD_TYPES.EMAIL].includes(field.type)) return "text";
  if (field.type === SEGMENT_FIELD_TYPES.COLLECTION) return "collection";
  return "field";
}

const NAVBAR_MEGA_MENU_PATHS = Object.freeze([
  "navigation.allProductsLabel",
  "navigation.megaMenuStyle",
  "navigation.megaMenuContent",
  "navigation.megaMenuDisplay",
  "navigation.megaMenuHierarchy",
  "navigation.showMegaMenuIntro",
  "navigation.megaMenuEyebrow",
  "navigation.megaMenuTitle",
  "navigation.megaMenuDescription",
  "navigation.showMegaMenuStats",
  "navigation.megaMenuCategoriesSubtitle",
  "navigation.megaMenuSortLabel",
  "navigation.showMegaMenuLatest",
  "navigation.megaMenuLatestLabel",
  "navigation.loadingCategoriesText",
  "navigation.emptyCategoriesText",
  "navigation.categoriesErrorText",
]);

function propertyAtPath(properties, path) {
  return properties.find((property) => property?.path === path) || null;
}

function itemFieldAtPath(property, index, key) {
  const source = (property?.item?.fields || []).find((field) => field?.key === key);
  return source
    ? { ...source, path: `${property.path}.${index}.${key}`, label: source.label || key }
    : null;
}

function navbarMegaMenuFields(properties) {
  const base = NAVBAR_MEGA_MENU_PATHS.map((path) => propertyAtPath(properties, path)).filter(Boolean);
  const visual = properties.filter((property) => String(property?.path || "").startsWith("navigation.megaMenuVisual."));
  return [...base, ...visual];
}

function descriptorFromElementTarget(context, target, canvasPath, selectedElement, properties) {
  if (!target) return null;
  const common = {
    ...context,
    label: target.label || "Elemento",
    canvasPath,
    previewDevice: selectedElement?.previewDevice || "desktop",
    chips: Array.isArray(target.chips) ? target.chips : [],
  };

  if (target.kind === "text") {
    const fieldPath = target.fieldPath || canvasPath;
    const field = propertyAtPath(properties, fieldPath);
    const device = selectedElement?.previewDevice === "mobile" ? "mobile" : "desktop";
    const colorPath = target.colorPathByDevice?.[device] || target.colorPath || field?.colorPath || "";
    const stylePrefix = String(target.stylePrefix || field?.stylePrefix || "");
    return {
      ...common,
      kind: "text",
      fieldPath,
      field,
      colorPath,
      styleFields: ["imageCarousel", "testimonials"].includes(String(context.sectionType || "")) && stylePrefix
        ? properties.filter((property) => String(property?.path || "").startsWith(stylePrefix))
        : [],
    };
  }

  if (target.kind === "image") {
    const fieldPath = target.fieldPath || canvasPath;
    const stylePrefix = String(target.stylePrefix || "");
    return {
      ...common,
      kind: "image",
      fieldPath,
      field: propertyAtPath(properties, fieldPath),
      mobileVariantField: target.mobileVariantPath ? propertyAtPath(properties, target.mobileVariantPath) : null,
      showField: target.showPath ? propertyAtPath(properties, target.showPath) : null,
      logoAppearance: target.logoAppearance || null,
      styleFields: stylePrefix
        ? properties.filter((property) => String(property?.path || "").startsWith(stylePrefix))
        : [],
    };
  }

  if (target.kind === "button") {
    const labelPath = target.labelPath || canvasPath;
    return {
      ...common,
      kind: "button",
      labelPath,
      fieldPath: labelPath,
      labelField: propertyAtPath(properties, labelPath),
      urlField: target.urlPath ? propertyAtPath(properties, target.urlPath) : null,
      showField: target.showPath ? propertyAtPath(properties, target.showPath) : null,
      extraFields: (target.extraPaths || []).map((path) => propertyAtPath(properties, path)).filter(Boolean),
      megaMenu: target.megaMenu === true,
      megaMenuFields: target.megaMenu === true ? navbarMegaMenuFields(properties) : [],
      styleFields: (() => {
        const device = selectedElement?.previewDevice === "mobile" ? "mobile" : "desktop";
        const prefix = target.stylePrefixByDevice?.[device] || target.stylePrefix || "";
        return prefix ? properties.filter((property) => String(property?.path || "").startsWith(prefix)) : [];
      })(),
      hasDedicatedStyle: Boolean(target.stylePrefix || target.stylePrefixByDevice),
    };
  }

  if (target.kind === "navbar-mobile") {
    const extraPrefixes = Array.isArray(target.extraPrefixes) ? target.extraPrefixes : [];
    const fields = properties.filter((property) => {
      const path = String(property?.path || "");
      return path.startsWith(target.fieldPrefix || "__never__") ||
        (target.extraPaths || []).includes(property?.path) ||
        extraPrefixes.some((prefix) => path.startsWith(prefix));
    });
    return { ...common, kind: "navbar-mobile", fields };
  }

  if (target.kind === "card-system") {
    const fieldPrefix = String(target.fieldPrefix || "catalog.cards.");
    const fields = properties.filter((property) => String(property?.path || "").startsWith(fieldPrefix));
    return { ...common, kind: "card-system", fieldPrefix, fields };
  }

  if (target.kind === "catalog-derived") {
    return { ...common, kind: "catalog-derived" };
  }

  if (target.kind === "catalog-item-naming") {
    return {
      ...common,
      kind: "catalog-item-naming",
      singularField: target.singularPath ? propertyAtPath(properties, target.singularPath) : null,
      pluralField: target.pluralPath ? propertyAtPath(properties, target.pluralPath) : null,
    };
  }

  if (target.kind === "contact-group") {
    const fields = (target.fieldPaths || []).map((path) => propertyAtPath(properties, path)).filter(Boolean);
    const imageField = target.imagePath ? propertyAtPath(properties, target.imagePath) : null;
    return {
      ...common,
      kind: "contact-group",
      editorMode: target.editorMode || "business",
      fields,
      imageField,
      imagePath: imageField?.path || target.imagePath || "",
    };
  }

  if (target.kind === "footer-group") {
    const fields = (target.fieldPaths || []).map((path) => propertyAtPath(properties, path)).filter(Boolean);
    return {
      ...common,
      kind: "footer-group",
      editorMode: target.editorMode || "brand",
      fields,
    };
  }

  if (target.kind === "floating-whatsapp") {
    const fields = (target.fieldPaths || []).map((path) => propertyAtPath(properties, path)).filter(Boolean);
    return {
      ...common,
      kind: "floating-whatsapp",
      fields,
    };
  }

  if (target.kind === "custom-embed") {
    const fieldPath = target.fieldPath || canvasPath;
    const stylePrefix = String(target.stylePrefix || "");
    return {
      ...common,
      kind: "custom-embed",
      editorMode: target.editorMode || "video",
      fieldPath,
      field: propertyAtPath(properties, fieldPath),
      styleFields: stylePrefix ? properties.filter((property) => String(property?.path || "").startsWith(stylePrefix)) : [],
    };
  }

  if (target.kind === "collection-manager") {
    const property = propertyAtPath(properties, target.collectionPath);
    const stylePrefix = String(target.stylePrefix || "");
    return {
      ...common,
      kind: "collection-manager",
      property,
      editorMode: target.editorMode || "",
      styleFields: stylePrefix
        ? properties.filter((propertyField) => String(propertyField?.path || "").startsWith(stylePrefix))
        : [],
    };
  }

  return { ...common, kind: target.kind || "field" };
}

function navbarCustomButtonStyleFields(index, device = "desktop") {
  const prefix = `navigation.buttons.${index}.${device === "mobile" ? "mobileStyle" : "desktopStyle"}.`;
  const defaults = device === "mobile"
    ? {
        textColor: "#1A1A2E", backgroundColor: "transparent", borderColor: "rgba(26,26,46,.09)",
        hoverTextColor: "#1A1A2E", hoverBackgroundColor: "#EEF0F5", hoverBorderColor: "rgba(26,26,46,.09)",
        activeTextColor: "#19242F", activeBackgroundColor: "rgba(0,0,0,.045)", activeBorderColor: "rgba(26,26,46,.09)",
        activeIndicatorColor: "#F35A3A", endIconColor: "#5B5D73",
      }
    : {
        textColor: "#5B5D73", backgroundColor: "transparent", borderColor: "rgba(26,26,46,.09)",
        hoverTextColor: "#19242F", hoverBackgroundColor: "rgba(0,0,0,.04)", hoverBorderColor: "#F35A3A",
        activeTextColor: "#19242F", activeBackgroundColor: "rgba(0,0,0,.045)", activeBorderColor: "rgba(26,26,46,.09)",
        activeIndicatorColor: "#F35A3A", endIconColor: "#5B5D73",
      };
  const labels = {
    textColor: "Texto", backgroundColor: "Fondo", borderColor: "Borde",
    hoverTextColor: "Texto hover", hoverBackgroundColor: "Fondo hover", hoverBorderColor: "Borde hover",
    activeTextColor: "Texto activo", activeBackgroundColor: "Fondo activo", activeBorderColor: "Borde activo",
    activeIndicatorColor: "Indicador activo", endIconColor: "Ícono",
  };
  const colors = Object.keys(labels).map((key) => ({
    path: `${prefix}${key}`,
    type: "color",
    label: `${device === "mobile" ? "Celular" : "Escritorio"} · ${labels[key]}`,
    explicitVisual: true,
    defaultValue: defaults[key],
    maxLength: 100,
    uiGroup: "design",
  }));
  if (device !== "mobile") return colors;
  return [
    ...colors,
    { path: `${prefix}focusColor`, type: "color", label: "Celular · Foco", explicitVisual: true, defaultValue: "#F35A3A", maxLength: 100, uiGroup: "design" },
    { path: `${prefix}fontSize`, type: "number", label: "Celular · Tamaño de texto (px)", explicitVisual: true, defaultValue: 16, limits: { min: 8, max: 40, step: 1 }, uiGroup: "design" },
    { path: `${prefix}fontWeight`, type: "number", label: "Celular · Peso", explicitVisual: true, defaultValue: 800, limits: { min: 100, max: 900, step: 100 }, uiGroup: "design" },
    { path: `${prefix}activeFontWeight`, type: "number", label: "Celular · Peso activo", explicitVisual: true, defaultValue: 900, limits: { min: 100, max: 900, step: 100 }, uiGroup: "design" },
    { path: `${prefix}minHeight`, type: "number", label: "Celular · Alto mínimo (px)", explicitVisual: true, defaultValue: 52, limits: { min: 32, max: 96, step: 1 }, uiGroup: "design" },
    { path: `${prefix}borderRadius`, type: "number", label: "Celular · Redondeado (px)", explicitVisual: true, defaultValue: 18, limits: { min: 0, max: 80, step: 1 }, uiGroup: "design" },
  ];
}

function navbarButtonDescriptor(context, collection, selectedElement, properties) {
  const { property, index } = collection;
  if (!property?.navbarButtons || !Number.isInteger(index)) return null;
  const labelField = itemFieldAtPath(property, index, "label");
  const behaviorField = itemFieldAtPath(property, index, "behavior");
  const targetField = itemFieldAtPath(property, index, "target");
  const megaMenuOverrideFields = [
    itemFieldAtPath(property, index, "megaMenuContent"),
    itemFieldAtPath(property, index, "megaMenuDisplay"),
    itemFieldAtPath(property, index, "megaMenuHierarchy"),
  ].filter(Boolean);
  return {
    ...context,
    kind: "button",
    label: `Botón ${index + 1}`,
    canvasPath: `navigation.buttons.${index}.label`,
    fieldPath: labelField?.path,
    labelPath: labelField?.path,
    labelField,
    behaviorField,
    targetField,
    megaMenuOverrideFields,
    megaMenuFields: navbarMegaMenuFields(properties),
    collection,
    previewDevice: selectedElement?.previewDevice || "desktop",
    chips: ["Desktop", "Mobile", "ID estable"],
    styleFields: [
      ...navbarCustomButtonStyleFields(index, "desktop"),
      ...navbarCustomButtonStyleFields(index, "mobile"),
    ],
    hasDedicatedStyle: true,
  };
}

export function resolveElementDescriptor(form, selectedElement) {
  if (!selectedElement?.sectionId) return null;
  const context = getElementSectionContext(form, selectedElement.sectionId);
  if (!context.definition) return null;
  const properties = (context.definition.properties || []).filter((property) => property?.editable !== false);

  if (selectedElement.elementId) {
    const registered = context.definition.elementTargets?.[`@${selectedElement.elementId}`];
    if (registered) {
      return descriptorFromElementTarget(
        context,
        registered,
        `@${selectedElement.elementId}`,
        selectedElement,
        properties
      );
    }
    const synthetic = SYNTHETIC_ELEMENT_DESCRIPTORS[selectedElement.elementId];
    if (!synthetic) return null;
    return {
      ...synthetic,
      ...context,
      elementId: selectedElement.elementId,
      previewDevice: selectedElement?.previewDevice || "desktop",
    };
  }

  const canvasPath = String(selectedElement.fieldPath || "").trim();
  if (!canvasPath) return null;
  const fieldPath = resolveCanvasPath(context, canvasPath);
  const registered = context.definition.elementTargets?.[canvasPath] || context.definition.elementTargets?.[fieldPath];
  if (registered) {
    return descriptorFromElementTarget(context, registered, canvasPath, selectedElement, properties);
  }

  const field = properties.find((property) => property.path === fieldPath) || null;
  const button = buttonDescriptor(properties, field, fieldPath);
  if (button) {
    return {
      ...context,
      ...button,
      canvasPath,
      fieldPath,
      previewDevice: selectedElement?.previewDevice || "desktop",
    };
  }

  const collection = collectionMatch(properties, fieldPath);
  if (collection) {
    const navbarButton = navbarButtonDescriptor(context, collection, selectedElement, properties);
    if (navbarButton) return navbarButton;

    if (context.definition?.elementEditingReady === true && collection.itemField) {
      const dynamicField = {
        ...collection.itemField,
        path: fieldPath,
        label: collection.itemField.label || collection.property.label || "Elemento",
      };
      if (collection.itemField.type === SEGMENT_FIELD_TYPES.IMAGE) {
        const imageField = imageFieldsFromRegistry(context.definition, form)
          .find((candidate) => candidate.path === fieldPath) || dynamicField;
        const itemFields = Array.isArray(collection.property?.item?.fields)
          ? collection.property.item.fields
          : [];
        const altSource = itemFields.find((candidate) => candidate.key === "alt")
          || itemFields.find((candidate) => /alt$/i.test(String(candidate?.key || "")))
          || null;
        const altField = altSource
          ? { ...altSource, path: `${collection.property.path}.${collection.index}.${altSource.key}`, label: altSource.label || "Texto alternativo" }
          : null;
        return {
          ...context,
          kind: "image",
          label: imageField.label || dynamicField.label,
          canvasPath,
          fieldPath,
          field: imageField,
          altField,
          collection,
          previewDevice: selectedElement?.previewDevice || "desktop",
          chips: [`Imagen ${collection.index + 1}`],
          styleFields: ["imageCarousel", "testimonials"].includes(String(context.sectionType || "")) && collection.itemField.stylePrefix
            ? properties.filter((property) => String(property?.path || "").startsWith(collection.itemField.stylePrefix))
            : [],
        };
      }
      if ([SEGMENT_FIELD_TYPES.TEXT, SEGMENT_FIELD_TYPES.TEXTAREA, SEGMENT_FIELD_TYPES.EMAIL].includes(collection.itemField.type)) {
        return {
          ...context,
          kind: "text",
          label: dynamicField.label,
          canvasPath,
          fieldPath,
          field: dynamicField,
          colorPath: collection.itemField.colorPath
            || (collection.itemField.colorKey
              ? `${collection.property.path}.${collection.index}.${collection.itemField.colorKey}`
              : ""),
          collection,
          previewDevice: selectedElement?.previewDevice || "desktop",
          styleFields: ["imageCarousel", "testimonials"].includes(String(context.sectionType || "")) && collection.itemField.stylePrefix
            ? properties.filter((property) => String(property?.path || "").startsWith(collection.itemField.stylePrefix))
            : [],
        };
      }
    }

    const collectionItemLabel = collection.property?.path === "catalog.brands.items"
      ? String(
          getAtPath(form, `${collection.property.path}.${collection.index}.name`, "")
          || getAtPath(form, `${collection.property.path}.${collection.index}.label`, "")
          || `Marca ${collection.index + 1}`
        )
      : collection.property?.path === "highlights"
        ? String(
            getAtPath(form, `${collection.property.path}.${collection.index}.title`, "")
            || `Diferencial ${collection.index + 1}`
          )
        : (collection.property.label || "Elemento");
    const sharedStylePrefix = context.sectionType === "testimonials"
      ? String(collection.property?.path || "").replace(/\.items$/, ".cardStyle.")
      : "";
    return {
      ...context,
      kind: "collection-item",
      label: collectionItemLabel,
      canvasPath,
      fieldPath,
      collection,
      previewDevice: selectedElement?.previewDevice || "desktop",
      styleFields: sharedStylePrefix
        ? properties.filter((propertyField) => String(propertyField?.path || "").startsWith(sharedStylePrefix))
        : [],
    };
  }

  if (!field) {
    return {
      ...context,
      kind: selectedElement.logicalType || "field",
      label: "Elemento",
      canvasPath,
      fieldPath,
      field: null,
      previewDevice: selectedElement?.previewDevice || "desktop",
    };
  }

  const resolvedField = field.type === SEGMENT_FIELD_TYPES.IMAGE
    ? (imageFieldsFromRegistry(context.definition, form).find((candidate) => candidate.path === fieldPath) || field)
    : field;

  const altField = resolvedField?.type === SEGMENT_FIELD_TYPES.IMAGE && resolvedField?.altPath
    ? propertyAtPath(properties, resolvedField.altPath)
    : null;

  return {
    ...context,
    kind: logicalKind(resolvedField),
    label: resolvedField.label || "Elemento",
    canvasPath,
    fieldPath,
    field: resolvedField,
    altField,
    previewDevice: selectedElement?.previewDevice || "desktop",
  };
}


const DIRECT_ELEMENT_EDITOR_KINDS = new Set([
  "button",
  "image",
  "collection-item",
  "collection-manager",
  "navbar-mobile",
  "card-system",
  "catalog-derived",
  "catalog-item-naming",
  "footer-group",
  "floating-whatsapp",
  "custom-embed",
  "contact-group",
]);

function descriptorHasDirectEditor(descriptor) {
  if (!descriptor) return false;
  if (descriptor.kind === "text") return Boolean(descriptor.field && descriptor.fieldPath);
  if (descriptor.kind === "image") return Boolean(descriptor.field && descriptor.fieldPath);
  if (descriptor.kind === "button") return Boolean(descriptor.labelField || descriptor.fieldPath);
  return DIRECT_ELEMENT_EDITOR_KINDS.has(descriptor.kind);
}

function descriptorCollectionItemExists(form, descriptor) {
  const collection = descriptor?.collection;
  if (!collection || !Number.isInteger(collection.index)) return true;
  const collectionPath = collection.property?.path;
  if (!collectionPath) return false;
  const items = getAtPath(form, collectionPath, []);
  return Array.isArray(items) && collection.index >= 0 && collection.index < items.length;
}

function linkedImageSelectionForAltPath(form, selectedElement, descriptor) {
  if (descriptor?.kind !== "text" || !descriptor?.fieldPath) return null;
  const context = getElementSectionContext(form, selectedElement.sectionId);
  const properties = (context.definition?.properties || []).filter((property) => property?.editable !== false);
  const altPath = descriptor.fieldPath;
  const imagePaths = [];

  properties.forEach((property) => {
    if (property?.type === SEGMENT_FIELD_TYPES.IMAGE && property.altPath === altPath && property.path) {
      imagePaths.push(property.path);
      return;
    }
    if (property?.type !== SEGMENT_FIELD_TYPES.COLLECTION || !property.path) return;
    const collection = collectionMatch(properties, altPath);
    if (!collection || collection.property !== property || !Number.isInteger(collection.index)) return;
    (property.item?.fields || []).forEach((itemField) => {
      if (itemField?.type === SEGMENT_FIELD_TYPES.IMAGE && itemField.key) {
        imagePaths.push(`${property.path}.${collection.index}.${itemField.key}`);
      }
    });
  });

  for (const imagePath of imagePaths) {
    const candidate = {
      ...selectedElement,
      fieldPath: imagePath,
      logicalType: "image",
    };
    const imageDescriptor = resolveElementDescriptor(form, candidate);
    if (
      imageDescriptor?.kind === "image" &&
      imageDescriptor?.field &&
      imageDescriptor?.altField?.path === altPath &&
      descriptorCollectionItemExists(form, imageDescriptor)
    ) {
      return candidate;
    }
  }
  return null;
}

export function resolveEditableElementSelection(form, selectedElement) {
  if (!selectedElement?.sectionId || !String(selectedElement?.fieldPath || "").trim()) return null;
  const context = getElementSectionContext(form, selectedElement.sectionId);
  if (context.definition?.elementEditingReady !== true) return null;

  const candidate = {
    ...selectedElement,
    sectionType: selectedElement.sectionType || context.sectionType,
    fieldPath: String(selectedElement.fieldPath).trim(),
    previewDevice: selectedElement.previewDevice || "desktop",
  };
  const descriptor = resolveElementDescriptor(form, candidate);
  if (!descriptor || !descriptorHasDirectEditor(descriptor) || !descriptorCollectionItemExists(form, descriptor)) {
    return null;
  }

  return linkedImageSelectionForAltPath(form, candidate, descriptor) || candidate;
}

export function sectionVisualPath(context) {
  return context?.layout?.index >= 0
    ? `layout.segments.${context.layout.index}.visualStyle`
    : "";
}

export function effectiveSectionPalette(form, context) {
  const global = {
    primary: form?.theme?.primaryColor || "#F35A3A",
    secondary: form?.theme?.secondaryColor || "#19242F",
    surface: form?.theme?.surfaceColor || "#FFFFFF",
    background: form?.theme?.backgroundColor || "#EEF0F5",
    text: form?.theme?.textColor || "#1A1A2E",
    mutedText: form?.theme?.mutedTextColor || "#5B5D73",
    divider: form?.theme?.dividerColor || "#E1E4EA",
    onPrimary: "#FFFFFF",
    onSecondary: "#FFFFFF",
  };
  const visual = context?.layout?.segment?.visualStyle || {};
  return visual.useGlobalColors === false ? { ...global, ...(visual.colors || {}) } : global;
}

function currentElementFineStyle(context, canvasPath) {
  const visual = context?.layout?.segment?.visualStyle || {};
  const style = visual?.elementColors?.[canvasPath];
  return style && typeof style === "object" ? style : {};
}

function buildElementFineVisualStyle(context, canvasPath, update) {
  const visual = context?.layout?.segment?.visualStyle || {};
  const elementColors = { ...(visual.elementColors || {}) };
  const current = elementColors[canvasPath] && typeof elementColors[canvasPath] === "object"
    ? { ...elementColors[canvasPath] }
    : {};
  update(current);
  if (Object.keys(current).length) elementColors[canvasPath] = current;
  else delete elementColors[canvasPath];
  const nextVisual = { ...visual };
  if (Object.keys(elementColors).length) nextVisual.elementColors = elementColors;
  else delete nextVisual.elementColors;
  return nextVisual;
}

export function currentElementColor(form, context, canvasPath) {
  return currentElementFineStyle(context, canvasPath).color || "";
}

export function currentElementFontScale(form, context, canvasPath) {
  const raw = currentElementFineStyle(context, canvasPath).fontScale;
  if (raw === "" || raw === null || raw === undefined) return null;
  const numeric = Number(raw);
  return Number.isFinite(numeric) ? clampHeroTextFontScale(numeric, 1) : null;
}

export function buildElementColorVisualStyle(form, context, canvasPath, nextColor) {
  return buildElementFineVisualStyle(context, canvasPath, (current) => {
    const normalized = String(nextColor || "").trim();
    if (normalized) current.color = normalized;
    else delete current.color;
  });
}

export function buildElementFontScaleVisualStyle(form, context, canvasPath, nextScale) {
  return buildElementFineVisualStyle(context, canvasPath, (current) => {
    const numeric = Number(nextScale);
    if (!Number.isFinite(numeric) || Math.abs(numeric - 1) < 0.001) {
      delete current.fontScale;
      return;
    }
    current.fontScale = clampHeroTextFontScale(numeric, 1);
  });
}

export function buildInternalDestinationOptions(form) {
  return getLandingSegments(form)
    .filter((segment) => segment?.visible !== false)
    .map((segment) => ({
      value: landingSegmentAnchor(segment),
      label: getSegmentDefinition(segment.type)?.label || segment.type || segment.id,
    }))
    .filter((option) => option.value && option.value !== "#");
}

export function validateElementEditorBindings(registry = SEGMENT_REGISTRY) {
  const issues = [];
  Object.entries(registry || {}).forEach(([sectionId, definition]) => {
    const seen = new Map();
    (definition?.properties || []).forEach((property) => {
      if (property?.editable === false || !property?.path) return;
      const concept = String(property.path);
      if (seen.has(concept)) {
        issues.push(`${sectionId}: binding duplicado para ${concept}`);
      } else {
        seen.set(concept, property);
      }
    });
  });
  return issues;
}

if (typeof process !== "undefined" && process.env?.NODE_ENV !== "production") {
  const duplicateBindings = validateElementEditorBindings();
  if (duplicateBindings.length) {
    throw new Error(`Landing Studio: ${duplicateBindings.join("; ")}`);
  }
}

export function getValueAtDescriptorPath(form, path, fallback = "") {
  return getAtPath(form, path, fallback);
}
